import { useEffect, useRef, useState } from "react";
import { Printer } from "lucide-react";
import { useRouter } from "../../store/router";
import { useI18n } from "../../i18n/useI18n";
import { RingSpinner } from "../../components/ui/RingSpinner";
import { HairlineProgress } from "../../components/ui/HairlineProgress";
import { useBoothConfig } from "../../store/booth";
import { ShareQrAside } from "../PrintShare/ShareQrAside";
import { AgentPrintView } from "./AgentPrintView";
import { trackError } from "../../telemetry/activity";

/** dialog 모드 전용 가짜 진행 시간 — agent 모드는 AgentPrintView 가 실제 큐 상태로 대체한다(BOOTH-39 3단계) */
const PRINT_FAKE_MS = 18_000;

/**
 * STAGE · Print(dialog 모드, HERIT 05b) — 링 스피너(프린터 아이콘) + "인쇄 중입니다." + 하단 PRINTING 진행 트랙.
 * 마운트 시 인쇄 시트 이미지 decode 를 기다려 window.print() 를 한 번 호출(StrictMode 이중 마운트 가드),
 * 다이얼로그 결과는 기다리지 않고(iPad 키오스크 브라우저는 억제할 수 있음) 고정 시간 진행 후 thanks 로.
 * 받기 화면의 QR 은 소형으로 이어서 보인다 (BOOTH-63 — 인쇄 중에도 폰으로 받을 수 있게).
 */
function DialogPrintView() {
  const go = useRouter((s) => s.go);
  const { t } = useI18n();
  const [pct, setPct] = useState(0);
  const printedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const img = document.getElementById("print-sheet-img") as HTMLImageElement | null;
      try {
        await img?.decode();
      } catch {
        // decode 실패해도 인쇄는 시도한다
      }
      await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));
      // 가드는 호출 직전에 — StrictMode 가 첫 effect 를 취소(cleanup)해도 두 번째 effect 가 정확히 한 번 인쇄한다
      if (cancelled || printedRef.current) return;
      printedRef.current = true;
      try {
        window.print();
      } catch (err) {
        console.warn("[Print] window.print 실패 — 진행만 계속", err);
        trackError("window_print_failed", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const startedAt = Date.now();
    const id = window.setInterval(() => {
      const p = Math.min(100, ((Date.now() - startedAt) / PRINT_FAKE_MS) * 100);
      setPct(p);
      if (p >= 100) {
        window.clearInterval(id);
        go("thanks");
      }
    }, 250);
    return () => window.clearInterval(id);
  }, [go]);

  return (
    <div className="view view--print">
      <div className="view__body center print-view">
        <RingSpinner size={140} accent progress={pct / 100}>
          <Printer size={48} strokeWidth={1.6} aria-hidden />
        </RingSpinner>
        <h1>{t.print.title}</h1>
        <p>{t.print.sub}</p>
        <i className="divider" aria-hidden />
        <ShareQrAside caption={t.print.delayedHint} />
      </div>
      <HairlineProgress className="print-view__progress" thick value={pct} label={t.print.status} />
    </div>
  );
}

/**
 * STAGE · Print — 부스 printMode 로 갈라진다. agent 는 서버 큐 폴링(AgentPrintView), 그 외(dialog·share 폴백)는
 * window.print() 스텁(DialogPrintView). agent 모드인데 printId 가 없으면(새로고침 등) 인쇄 요청 없이 온 것이라 처음으로.
 */
export function PrintView() {
  const printMode = useBoothConfig((s) => s.config?.booth.printMode ?? "dialog");
  const printId = useRouter((s) => s.printId);
  const resetSession = useRouter((s) => s.resetSession);
  const agent = printMode === "agent";
  useEffect(() => {
    if (agent && !printId) resetSession();
  }, [agent, printId, resetSession]);
  if (agent) return printId ? <AgentPrintView printId={printId} /> : null;
  return <DialogPrintView />;
}
